export enum RequestStatus {
  Draft = 'draft',
  InProgress = 'in_progress',
  Completed = 'completed',
  Rejected = 'rejected',
  Cancelled = 'cancelled',
  Expired = 'expired',
  /**
   * A Draft the requester deleted before anyone else ever saw or acted on it.
   * Deliberately distinct from Cancelled — Cancelled means a request that had
   * (or could have had) real participant engagement was called off; Deleted
   * means it was a one-person mistake with zero engagement. Usage dashboards
   * and metrics must never count Deleted requests as cancellations. Reachable
   * only from Draft — see M4-24. Never shown in any staff-facing list.
   */
  Deleted = 'deleted',
}
export type RequestStatusType = `${RequestStatus}`;

export const REQUEST_TERMINAL_STATUSES = [
  RequestStatus.Completed,
  RequestStatus.Rejected,
  RequestStatus.Cancelled,
  RequestStatus.Expired,
  RequestStatus.Deleted,
] as const;

export const REQUEST_STATUS_ORDERS: RequestStatus[] = [
  RequestStatus.Draft,
  RequestStatus.InProgress,
  RequestStatus.Completed,
  RequestStatus.Rejected,
  RequestStatus.Cancelled,
  RequestStatus.Expired,
];

export type RequestTerminalStatus = (typeof REQUEST_TERMINAL_STATUSES)[number];

export enum RequestActorType {
  Internal = 'internal',
  External = 'external',
  System = 'system',
}

export enum RequestEventType {
  DraftSaved = 'DRAFT_SAVED',
  SentToStep = 'SENT_TO_STEP',
  StepOpened = 'STEP_OPENED',
  StepSubmitted = 'STEP_SUBMITTED',
  StepCompleted = 'STEP_COMPLETED',
  StepRejected = 'STEP_REJECTED',
  RequestCancelled = 'REQUEST_CANCELLED',
  /** A Draft was deleted by its requester before anyone else engaged with it — see RequestStatus.Deleted. */
  DraftDeleted = 'DRAFT_DELETED',
  RequestExpired = 'REQUEST_EXPIRED',
  RequestCompleted = 'REQUEST_COMPLETED',
  LockAcquired = 'LOCK_ACQUIRED',
  LockReleased = 'LOCK_RELEASED',
  ForceUnlock = 'FORCE_UNLOCK',
  ArtifactGenerated = 'ARTIFACT_GENERATED',
  EmailFailed = 'EMAIL_FAILED',
  ReminderSent = 'REMINDER_SENT',
  StepSkipped = 'STEP_SKIPPED',
  StepReassigned = 'STEP_REASSIGNED',
  /** Staff manually re-triggered the sign-invitation email for an Active ExternalParticipant step. */
  InvitationResent = 'INVITATION_RESENT',
  /** Staff manually re-sent the completed-document email/link for a Completed ExternalParticipant step. */
  DocumentResent = 'DOCUMENT_RESENT',
  // ── Queue / Lock events (Module 5) ──────────────────────────────────────────
  /** Lock released by the server-side expiry sweep (M5-04). Distinct from
   *  LockReleased (voluntary release) so audit queries can distinguish them. */
  LockReleasedBySweep = 'LOCK_RELEASED_BY_SWEEP',
  /** A QueueReview step became active with zero eligible queue members (M5-05).
   *  Written at escalation time; triggers admin notification. */
  QueueEscalation = 'QUEUE_ESCALATION',
  /** The BLOCKED_NO_QUEUE_MEMBERS flag was automatically cleared after a staff
   *  member was added to a qualifying department (M5-05 auto-unblock). */
  QueueBlockCleared = 'QUEUE_BLOCK_CLEARED',
  // ── Offline conflict resolution (Module 7) ───────────────────────────────────
  /** Field staff chose to keep their local offline draft, overwriting the server version. */
  ConflictKeptLocal = 'CONFLICT_KEPT_LOCAL',
  /** Field staff chose to discard their local offline draft and use the server version. */
  ConflictKeptServer = 'CONFLICT_KEPT_SERVER',
  /** A field owned by an earlier step was edited by a later step via editableByStepIds
   *  (e.g. an external signer correcting a value the original requester entered). One
   *  event per changed field — meta carries stepId (the field's owning step), fieldKey,
   *  fieldLabel, oldValue, newValue. */
  FieldValueChanged = 'FIELD_VALUE_CHANGED',
}
